// Settings-03b — multi-role selector used by the Invite + Edit
// Team Member modals. Renders one checkbox per product role and
// emits the resulting array on every change. ``Producer`` is a
// separate business flag (Sales CRM access bridge) and lives
// outside this component so the visual grouping stays correct.

import { useMemo } from "react";

import type { BrokerProductRole } from "./types";


interface RoleOption {
  value: BrokerProductRole;
  label: string;
  description: string;
}


// The order is intentional (highest privilege first) so the column
// reads naturally in the modal.
export const ROLE_OPTIONS: ReadonlyArray<RoleOption> = [
  {
    value: "owner",
    label: "Owner",
    description:
      "Full control. Manages billing, broker settings, and the team.",
  },
  {
    value: "admin",
    label: "Admin",
    description:
      "Manages team members, locations, and account team assignments.",
  },
  {
    value: "member",
    label: "Member",
    description:
      "Default role. Reaches dashboard, accounts, commitments, playbooks.",
  },
  {
    value: "sales",
    label: "Sales",
    description:
      "Reaches the Sales CRM / Prospects surface alongside core operations.",
  },
];


export interface RoleCheckboxesProps {
  /** Currently selected roles. */
  value: BrokerProductRole[];
  /** Called with the new full role array on every toggle. */
  onChange: (next: BrokerProductRole[]) => void;
  /**
   * When true, the ``owner`` checkbox is disabled. The Settings-03b
   * server-side guardrail blocks non-owner callers from granting
   * or removing the owner role; the FE mirrors that disable so a
   * non-owner admin doesn't see a tickbox they can't actually use.
   */
  disableOwnerToggle?: boolean;
  /** Test hook prefix applied to each checkbox. */
  testIdPrefix?: string;
}


export function RoleCheckboxes({
  value, onChange,
  disableOwnerToggle = false,
  testIdPrefix = "role-checkbox",
}: RoleCheckboxesProps) {
  const selected = useMemo(() => new Set(value), [value]);

  const toggle = (role: BrokerProductRole) => {
    const next = new Set(selected);
    if (next.has(role)) {
      next.delete(role);
    } else {
      next.add(role);
    }
    // Preserve the canonical option order so two memberships with
    // the same role-set serialise identically.
    onChange(
      ROLE_OPTIONS
        .filter((o) => next.has(o.value))
        .map((o) => o.value),
    );
  };

  return (
    <fieldset
      data-testid={`${testIdPrefix}-fieldset`}
      className="flex flex-col gap-2"
    >
      <legend className="text-sm font-medium text-text-primary">
        Roles
      </legend>
      <p className="text-xs text-text-secondary">
        A team member can hold more than one role.
      </p>
      <div className="flex flex-col gap-2">
        {ROLE_OPTIONS.map((opt) => {
          const checked = selected.has(opt.value);
          const disabled =
            opt.value === "owner" && disableOwnerToggle;
          return (
            <label
              key={opt.value}
              data-testid={`${testIdPrefix}-${opt.value}-row`}
              className={[
                "flex items-start gap-2 rounded-md border border-border-subtle px-3 py-2",
                disabled
                  ? "cursor-not-allowed opacity-60"
                  : "cursor-pointer hover:border-border-strong",
              ].join(" ")}
            >
              <input
                type="checkbox"
                data-testid={`${testIdPrefix}-${opt.value}`}
                checked={checked}
                disabled={disabled}
                onChange={() => toggle(opt.value)}
                className="mt-1"
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-text-primary">
                  {opt.label}
                </span>
                <span className="text-xs text-text-secondary">
                  {opt.description}
                </span>
              </div>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
